import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Alert,
  Container,
  FormFeedback,
  Input,
  Label,
  Form,
} from 'reactstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import logo from '../../assets/images/NGK-LOGO.png';
import CarouselPage from '../AuthenticationInner/CarouselPage';
import { resetUserPassword } from '../../helpers/wordpress_password';

const ResetPasswordPage = () => {
  document.title = 'Set New Password | NGK Tours';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const login = useMemo(
    () => searchParams.get('login') || searchParams.get('username') || '',
    [searchParams]
  );
  const key = useMemo(() => searchParams.get('key') || '', [searchParams]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Please enter a new password'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm your password'),
    }),
    onSubmit: async (values) => {
      setError(null);
      setSuccess(null);

      if (!login || !key) {
        setError('This reset link is invalid or incomplete. Please request a new one.');
        return;
      }

      setSubmitting(true);
      try {
        await resetUserPassword(login, key, values.password);
        setSuccess('Password updated successfully. You can sign in now.');
        setTimeout(() => navigate('/login'), 1800);
      } catch (err: any) {
        setError(err?.message || 'Unable to reset password.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <React.Fragment>
      <div className="auth-page">
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={4} md={5} className="col-xxl-3">
              <div className="auth-full-page-content d-flex p-sm-5 p-4">
                <div className="w-100">
                  <div className="d-flex flex-column h-100">
                    <div className="mb-4 mb-md-5 text-center">
                      <Link to="/login" className="d-block auth-logo">
                        <img src={logo} alt="NGK Tours" height="28" />
                      </Link>
                    </div>
                    <div className="auth-content my-auto">
                      <div className="text-center">
                        <h5 className="mb-0">Set New Password</h5>
                        <p className="text-muted mt-2">
                          Choose a strong password for your NGK Tours account.
                        </p>
                      </div>

                      {(!login || !key) && (
                        <Alert color="warning" className="mt-3">
                          Missing reset key. Open the link from your email, or{' '}
                          <Link to="/recoverpw">request a new reset</Link>.
                        </Alert>
                      )}

                      {error && (
                        <Alert color="danger" className="mt-3">
                          {error}
                        </Alert>
                      )}
                      {success && (
                        <Alert color="success" className="mt-3">
                          {success}
                        </Alert>
                      )}

                      <Form
                        className="custom-form mt-4"
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">New password</Label>
                          <Input
                            name="password"
                            type="password"
                            placeholder="Enter new password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={!!(validation.touched.password && validation.errors.password)}
                            disabled={!login || !key || submitting}
                          />
                          {validation.touched.password && validation.errors.password && (
                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Confirm password</Label>
                          <Input
                            name="confirmPassword"
                            type="password"
                            placeholder="Confirm new password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword}
                            invalid={
                              !!(
                                validation.touched.confirmPassword &&
                                validation.errors.confirmPassword
                              )
                            }
                            disabled={!login || !key || submitting}
                          />
                          {validation.touched.confirmPassword &&
                            validation.errors.confirmPassword && (
                              <FormFeedback type="invalid">
                                {validation.errors.confirmPassword}
                              </FormFeedback>
                            )}
                        </div>

                        <Row className="mb-3">
                          <Col>
                            <button
                              className="btn btn-primary w-100 waves-effect waves-light"
                              type="submit"
                              disabled={!login || !key || submitting}
                            >
                              {submitting ? 'Saving...' : 'Update Password'}
                            </button>
                          </Col>
                        </Row>
                      </Form>

                      <div className="mt-4 text-center">
                        <p className="text-muted mb-0">
                          <Link to="/login" className="text-primary fw-semibold">
                            Back to Sign In
                          </Link>
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 mt-md-5 text-center">
                      <p className="mb-0">
                        © {new Date().getFullYear()} NGK Tours
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <CarouselPage />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ResetPasswordPage;
